'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { addDoc, collection, firestore, limit, onSnapshot, orderBy, query, serverTimestamp } from '@/lib/db'

// =============================================================================================
// LUDO VOICE - talk to the other players at the table (online rooms only).
//
// Mesh WebRTC (max 4 players = 3 links each). Signalling goes through the room itself:
//   ludoRooms/{roomId}/voice/*   { kind: hello | bye | offer | answer, from, sid, to, toSid, data, createdAt }
// Offers / answers carry the FULL session description (candidates included), so a link needs only 2 docs.
// Who offers: whoever pressed "Voice" LATER sends the offer to everyone who is already in voice (its `hello`
// sits after theirs in the room log), so both sides never offer at once.
// =============================================================================================

const iceServers = (): RTCIceServer[] => {
  const list: RTCIceServer[] = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }]
  const url = process.env.NEXT_PUBLIC_TURN_URL
  if (url) {
    list.push({
      urls: url.split(',').map((u) => u.trim()).filter(Boolean),
      username: process.env.NEXT_PUBLIC_TURN_USERNAME || '',
      credential: process.env.NEXT_PUBLIC_TURN_CREDENTIAL || '',
    })
  }
  return list
}

const AUDIO: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 }
const CONNECT_TIMEOUT_MS = 25_000

type VoiceDoc = { id: string; kind: string; from: string; sid: string; to: string; toSid: string; data: string; at: number }
type Link = { uid: string; sid: string; pc: RTCPeerConnection; audio: HTMLAudioElement; stream: MediaStream | null; timer: number; done: boolean }

const newSid = () => Math.random().toString(36).slice(2, 12) + Date.now().toString(36)

/** Resolves once ICE gathering is done (or after `ms`), so the description we send already holds every candidate. */
function gathered(pc: RTCPeerConnection, ms = 2500): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve()
  return new Promise((res) => {
    const finish = () => { pc.removeEventListener('icegatheringstatechange', on); window.clearTimeout(t); res() }
    const on = () => { if (pc.iceGatheringState === 'complete') finish() }
    const t = window.setTimeout(finish, ms)
    pc.addEventListener('icegatheringstatechange', on)
  })
}

export type LudoVoice = {
  supported: boolean
  joined: boolean
  joining: boolean
  micOn: boolean
  error: string
  /** uids we are actually connected to (audio flowing). */
  connected: Set<string>
  /** uids currently speaking (including me while my mic is open). */
  speaking: Set<string>
  join: () => Promise<void>
  leave: () => void
  toggleMic: () => void
}

export type VoiceOpts = {
  /** Parent collection that holds `{id}/voice` signalling docs. Default: ludoRooms. Group voice rooms use groupChats. */
  collection?: string
  /** Ignore signalling older than this (a long-lived group has old hello/offer docs lying around). */
  maxAgeMs?: number
  /** How many signalling docs to read (newest first). */
  maxDocs?: number
}

/** Voice room for a whole group chat: same mesh WebRTC as Ludo, signalling in groupChats/{id}/voice. */
export const useGroupVoice = (groupId: string, meUid: string, enabled: boolean): LudoVoice =>
  useLudoVoice(groupId, meUid, enabled, { collection: 'groupChats', maxAgeMs: 3 * 60 * 60 * 1000, maxDocs: 120 })

export function useLudoVoice(roomId: string, meUid: string, enabled: boolean, opts?: VoiceOpts): LudoVoice {
  const baseCol = opts?.collection || 'ludoRooms'
  const maxAge = opts?.maxAgeMs || 0
  const maxDocs = opts?.maxDocs || 300
  const supported = typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof RTCPeerConnection !== 'undefined'
  const [joined, setJoined] = useState(false)
  const [joining, setJoining] = useState(false)
  const [micOn, setMicOn] = useState(true)
  const [error, setError] = useState('')
  const [connected, setConnected] = useState<Set<string>>(new Set())
  const [speaking, setSpeaking] = useState<Set<string>>(new Set())

  const sid = useRef('')
  const stream = useRef<MediaStream | null>(null)
  const links = useRef<Map<string, Link>>(new Map())
  const handled = useRef<Set<string>>(new Set())
  const unsub = useRef<(() => void) | null>(null)
  const actx = useRef<AudioContext | null>(null)
  const meters = useRef<Map<string, { an: AnalyserNode; src: MediaStreamAudioSourceNode; buf: Uint8Array<ArrayBuffer> }>>(new Map())
  const micRef = useRef(true); micRef.current = micOn
  const aliveRef = useRef(false)

  const send = useCallback(async (kind: string, to: string, toSid: string, data: string) => {
    try {
      await addDoc(collection(firestore, baseCol, roomId, 'voice'), { kind, from: meUid, sid: sid.current, to, toSid, data, createdAt: serverTimestamp() })
    } catch { /* not in the room any more */ }
  }, [roomId, meUid, baseCol])

  const syncConnected = () => {
    const s = new Set<string>()
    links.current.forEach((l) => { if (l.pc.connectionState === 'connected') s.add(l.uid) })
    setConnected(s)
  }

  const meter = (key: string, ms: MediaStream) => {
    try {
      if (!actx.current) { const AC = (window as any).AudioContext || (window as any).webkitAudioContext; actx.current = new AC() }
      const c = actx.current!
      const src = c.createMediaStreamSource(ms); const an = c.createAnalyser(); an.fftSize = 512
      src.connect(an)
      meters.current.set(key, { an, src, buf: new Uint8Array(new ArrayBuffer(an.fftSize)) })
    } catch { /* speaking ring is just a nicety */ }
  }
  const unmeter = (key: string) => { const m = meters.current.get(key); if (m) { try { m.src.disconnect() } catch { /* ignore */ } meters.current.delete(key) } }

  const closeLink = useCallback((uid: string) => {
    const l = links.current.get(uid); if (!l) return
    window.clearTimeout(l.timer)
    l.pc.ontrack = null; l.pc.onconnectionstatechange = null
    try { l.pc.close() } catch { /* ignore */ }
    try { l.audio.pause(); l.audio.srcObject = null } catch { /* ignore */ }
    unmeter(uid)
    links.current.delete(uid)
    syncConnected()
  }, [])

  const makeLink = useCallback((uid: string, peerSid: string): Link => {
    closeLink(uid)
    const pc = new RTCPeerConnection({ iceServers: iceServers() })
    const audio = new Audio(); audio.autoplay = true; (audio as any).playsInline = true
    const link: Link = { uid, sid: peerSid, pc, audio, stream: null, done: false, timer: 0 }
    stream.current?.getTracks().forEach((t) => pc.addTrack(t, stream.current!))
    pc.ontrack = (e) => {
      const ms = e.streams[0] || new MediaStream([e.track])
      link.stream = ms; audio.srcObject = ms
      void audio.play().catch(() => undefined)
      unmeter(uid); meter(uid, ms)
    }
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') { link.done = true; window.clearTimeout(link.timer) }
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') { if (links.current.get(uid) === link) closeLink(uid) }
      syncConnected()
    }
    // a link that never comes up is dropped; it is rebuilt when that player presses Voice again
    link.timer = window.setTimeout(() => { if (!link.done && links.current.get(uid) === link) closeLink(uid) }, CONNECT_TIMEOUT_MS)
    links.current.set(uid, link)
    return link
  }, [closeLink])

  const offerTo = useCallback(async (uid: string, peerSid: string) => {
    const link = makeLink(uid, peerSid)
    try {
      const offer = await link.pc.createOffer({ offerToReceiveAudio: true })
      await link.pc.setLocalDescription(offer)
      await gathered(link.pc)
      if (links.current.get(uid) !== link || !aliveRef.current) return
      await send('offer', uid, peerSid, JSON.stringify(link.pc.localDescription))
    } catch { closeLink(uid) }
  }, [makeLink, send, closeLink])

  const answerTo = useCallback(async (d: VoiceDoc) => {
    const link = makeLink(d.from, d.sid)
    try {
      await link.pc.setRemoteDescription(JSON.parse(d.data))
      const ans = await link.pc.createAnswer()
      await link.pc.setLocalDescription(ans)
      await gathered(link.pc)
      if (links.current.get(d.from) !== link || !aliveRef.current) return
      await send('answer', d.from, d.sid, JSON.stringify(link.pc.localDescription))
    } catch { closeLink(d.from) }
  }, [makeLink, send, closeLink])

  // Re-evaluates the whole room log on every snapshot (idempotent), so nothing depends on snapshot timing.
  const reconcile = useCallback((docs: VoiceDoc[]) => {
    if (!aliveRef.current) return
    const mySid = sid.current
    let sawMine = false
    const peers = new Map<string, { sid: string; earlier: boolean }>() // players currently in voice (latest hello / bye wins)
    for (const d of docs) {
      if (d.from === meUid) { if (d.kind === 'hello' && d.sid === mySid) sawMine = true; continue }
      if (d.kind === 'hello') peers.set(d.from, { sid: d.sid, earlier: !sawMine })
      else if (d.kind === 'bye') { const p = peers.get(d.from); if (p && p.sid === d.sid) peers.delete(d.from) }
    }
    if (!sawMine) return // my own hello is not visible yet - order is not known
    // drop links to players who left or came back with a new session
    links.current.forEach((l, uid) => { const p = peers.get(uid); if (!p || p.sid !== l.sid) closeLink(uid) })
    // I joined after them: I make the offer
    peers.forEach((p, uid) => { if (p.earlier && !links.current.has(uid) && !handled.current.has(`o:${uid}:${p.sid}`)) { handled.current.add(`o:${uid}:${p.sid}`); void offerTo(uid, p.sid) } })
    // offers / answers addressed to this session
    for (const d of docs) {
      if (d.to !== meUid || d.toSid !== mySid || handled.current.has(d.id)) continue
      const p = peers.get(d.from); if (!p || p.sid !== d.sid) continue
      if (d.kind === 'offer') { handled.current.add(d.id); void answerTo(d) }
      else if (d.kind === 'answer') {
        const l = links.current.get(d.from)
        if (l && l.sid === d.sid && !l.pc.currentRemoteDescription && l.pc.signalingState === 'have-local-offer') {
          handled.current.add(d.id)
          void l.pc.setRemoteDescription(JSON.parse(d.data)).catch(() => closeLink(d.from))
        }
      }
    }
  }, [meUid, offerTo, answerTo, closeLink])

  const leave = useCallback(() => {
    if (!aliveRef.current && !stream.current) return
    const wasAlive = aliveRef.current
    aliveRef.current = false
    unsub.current?.(); unsub.current = null
    if (wasAlive && sid.current) void send('bye', '', '', '')
    Array.from(links.current.keys()).forEach(closeLink)
    stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null
    unmeter('me')
    try { void actx.current?.close() } catch { /* ignore */ }
    actx.current = null; meters.current.clear(); handled.current.clear()
    setJoined(false); setJoining(false); setConnected(new Set()); setSpeaking(new Set())
  }, [send, closeLink])

  const join = useCallback(async () => {
    if (!enabled || joined || joining) return
    setError('')
    if (!supported) { setError('Voice is not available on this device.'); return }
    setJoining(true)
    try {
      const ms = await navigator.mediaDevices.getUserMedia({ audio: AUDIO, video: false })
      stream.current = ms
      ms.getAudioTracks().forEach((t) => { t.enabled = micRef.current })
      sid.current = newSid(); aliveRef.current = true
      meter('me', ms)
      unsub.current = onSnapshot(
        query(collection(firestore, baseCol, roomId, 'voice'), orderBy('createdAt', 'desc'), limit(maxDocs)),
        (snap) => {
          const docs: VoiceDoc[] = snap.docs.map((x) => {
            const v = x.data() as any
            return { id: x.id, kind: String(v.kind || ''), from: String(v.from || ''), sid: String(v.sid || ''), to: String(v.to || ''), toSid: String(v.toSid || ''), data: String(v.data || ''), at: v.createdAt?.toMillis?.() ?? 0 }
          }).filter((x) => !maxAge || x.at === 0 || Date.now() - x.at < maxAge).reverse() // oldest first (and, for long-lived rooms, only recent signalling)
          reconcile(docs)
        },
      )
      await send('hello', '', '', '')
      setJoined(true)
    } catch (err) {
      const name = (err as { name?: string })?.name
      stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null
      aliveRef.current = false; unsub.current?.(); unsub.current = null
      setError(name === 'NotAllowedError' || name === 'SecurityError' ? 'Microphone permission is blocked.' : name === 'NotFoundError' ? 'No microphone found.' : 'Could not start voice.')
    }
    setJoining(false)
  }, [enabled, joined, joining, supported, roomId, send, reconcile, baseCol, maxAge, maxDocs])

  const toggleMic = useCallback(() => {
    setMicOn((on) => {
      const next = !on
      stream.current?.getAudioTracks().forEach((t) => { t.enabled = next })
      return next
    })
  }, [])

  // who is talking (drives the glowing ring on the player panels)
  useEffect(() => {
    if (!joined) return
    const t = window.setInterval(() => {
      const next = new Set<string>()
      meters.current.forEach((m, key) => {
        if (key === 'me' && !micRef.current) return
        m.an.getByteTimeDomainData(m.buf)
        let peak = 0
        for (let i = 0; i < m.buf.length; i++) peak = Math.max(peak, Math.abs(m.buf[i] - 128))
        if (peak > 9) next.add(key === 'me' ? meUid : key)
      })
      setSpeaking((cur) => (cur.size === next.size && [...next].every((x) => cur.has(x)) ? cur : next))
    }, 160)
    return () => window.clearInterval(t)
  }, [joined, meUid])

  // leaving the table (or the room changing) always closes the mic
  const leaveRef = useRef(leave); leaveRef.current = leave
  useEffect(() => {
    const onHide = () => leaveRef.current()
    window.addEventListener('pagehide', onHide)
    return () => { window.removeEventListener('pagehide', onHide); leaveRef.current() }
  }, [roomId])

  return { supported: supported && enabled, joined, joining, micOn, error, connected, speaking, join, leave, toggleMic }
}
